import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { salesService } from '../../services/salesService';
import { contactsService } from '../../services/contactsService';
import { PDFDownloadLink } from '@react-pdf/renderer';
import OrderReceipt from './OrderReceipt';
import { 
  PrinterIcon, 
  PencilIcon, 
  ArrowLeftIcon 
} from '@heroicons/react/24/outline';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase';
import EditOrderModal from './EditOrderModal';

export default function OrderDetailsPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [businessDetails, setBusinessDetails] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log('Fetching order with ID:', orderId);
        const orderData = await salesService.getOrder(orderId);
        console.log('Fetched order data:', orderData);
        setOrder(orderData);

        // Fetch customer data
        if (orderData.customerId) {
          console.log('Fetching customer data for ID:', orderData.customerId);
          const customer = await contactsService.getContact(orderData.customerId);
          console.log('Fetched customer data:', customer);
          setCustomerData(customer);
        }

        // Fetch business details
        const businessDoc = await getDoc(doc(db, 'settings', 'business'));
        if (businessDoc.exists()) {
          setBusinessDetails(businessDoc.data());
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [orderId]);

  const handleOrderUpdated = (updatedOrder) => {
    setOrder(updatedOrder);
    setShowEditModal(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500 dark:text-gray-400">Order not found</p>
      </div>
    );
  }

  return (
    <div className="h-full">
      <div className="bg-white dark:bg-gray-800 h-full sm:rounded-lg sm:shadow-lg sm:m-4">
        <div className="p-3 sm:p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
            <button
              onClick={() => navigate('/sales')}
              className="flex items-center text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
            >
              <ArrowLeftIcon className="h-5 w-5 mr-2" />
              Back to Sales
            </button>
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
              <button 
                onClick={() => setShowEditModal(true)}
                className="w-full sm:w-auto bg-yellow-600 text-white px-4 py-2 rounded-md hover:bg-yellow-700 flex items-center justify-center"
              >
                <PencilIcon className="h-5 w-5 mr-2" />
                Edit Order
              </button>
              <PDFDownloadLink 
                document={
                  <OrderReceipt 
                    order={order} 
                    customerData={customerData}
                    businessDetails={businessDetails} 
                  />
                }
                fileName={`invoice-${order.id}.pdf`}
                className="w-full sm:w-auto"
              >
                {({ loading: pdfLoading }) => (
                  <button 
                    className="w-full bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 flex items-center justify-center"
                    disabled={pdfLoading}
                  >
                    <PrinterIcon className="h-5 w-5 mr-2" />
                    {pdfLoading ? 'Generating...' : 'Print Receipt'}
                  </button>
                )}
              </PDFDownloadLink>
            </div>
          </div>
        </div>

        <div className="p-3 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Customer Details</h3>
              <p className="text-lg text-gray-900 dark:text-white">{order.customerName}</p>
              {customerData && (
                <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  <p>Phone: {customerData.phone}</p>
                  <p>Address: {customerData.address}</p>
                  <p>Email: {customerData.email}</p>
                </div>
              )}
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Order Details</h3>
              <p className="text-lg text-gray-900 dark:text-white">Order #{order.id}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Date: {order.finalizedAt ? new Date(order.finalizedAt).toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric'
                }) : 'Pending'}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Status: {order.status}
              </p>
            </div>
          </div>

          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Items</h3>
            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Item
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Qty
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Price
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {order.items.map((item, index) => (
                    <tr key={index}>
                      <td className="px-4 sm:px-6 py-4 text-sm text-gray-900 dark:text-white">
                        {item.vegetable}
                      </td>
                      <td className="px-4 sm:px-6 py-4 text-sm text-right text-gray-900 dark:text-white">
                        {item.actualQuantity || item.estimatedQuantity} {item.unit}
                      </td>
                      <td className="px-4 sm:px-6 py-4 text-sm text-right text-gray-900 dark:text-white">
                        ₹{item.pricePerKg || '-'}
                      </td>
                      <td className="px-4 sm:px-6 py-4 text-sm text-right text-gray-900 dark:text-white">
                        {item.pricePerKg ? `₹${(item.actualQuantity * item.pricePerKg).toLocaleString()}` : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {order.status === 'completed' && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-3 max-w-md ml-auto">
              <div className="flex justify-between">
                <span className="text-gray-500">Subtotal</span>
                <span className="text-gray-900 dark:text-white">₹{order.subtotal?.toLocaleString() || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Discount</span>
                <span className="text-gray-900 dark:text-white">₹{order.discount?.toLocaleString() || '0'}</span>
              </div>
              <div className="flex justify-between text-lg font-medium">
                <span className="text-gray-900 dark:text-white">Total</span>
                <span className="text-gray-900 dark:text-white">₹{order.totalAmount?.toLocaleString() || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Received</span>
                <span className="text-gray-900 dark:text-white">₹{order.amountReceived?.toLocaleString() || '0'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Balance</span>
                <span className={order.remainingBalance > 0 ? 'text-red-600' : 'text-green-600'}>
                  ₹{Math.abs(order.remainingBalance || 0).toLocaleString()}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
      {showEditModal && (
        <EditOrderModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          order={order}
          onOrderUpdated={handleOrderUpdated}
        />
      )}
    </div>
  );
}